// ./src/hooks/useServiceIntervalAccounts.ts
//
// Hook for managing service interval accounts (CRUD + list).
//
// Follows the same architecture as useExpenses:
// - OpResult returns from all operations
// - ApiError for structured error handling
// - Race-condition protection via monotonic call IDs
// - Mount-time refs for configuration (pageSize, initialFilters, initialSort)
// - userId sourced from useApp() context
// - Memoized return object

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  ServiceIntervalAccount,
  ServiceIntervalAccountService,
  ServiceIntervalAccountFilter,
  ServiceIntervalAccountInput,
  ServiceIntervalAccountWhereInput,
  Pagination,
  SortItem,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ============================================
// Types
// ============================================

export interface UseServiceIntervalAccountsOptions {
  /**
   * Page size for pagination.
   *
   * This is a **mount-time** configuration value.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId/carId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply new filters, call `list(newFilters)` explicitly.
   */
  initialFilters?: Partial<ServiceIntervalAccountFilter>;

  /**
   * Sort configuration applied on initial autoLoad and userId/carId-change reloads.
   *
   * This is a **mount-time** configuration value.
   *
   * @default [{ name: "kindId", order: "ASC" }]
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId/carId change) */
  autoLoad?: boolean;

  /** Car ID to load intervals for */
  carId?: string | null;
}

export interface UseServiceIntervalAccountsReturn {
  // Data state
  serviceIntervalAccounts: ServiceIntervalAccount[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<ServiceIntervalAccountFilter>;
  sortBy: SortItem[];

  // Pagination
  pageSize: number;

  // Operations state
  isSaving: boolean;
  removingKindId: number | null;
  selectedServiceIntervalAccount: ServiceIntervalAccount | null;

  // Core operations
  list: (
    filter: ServiceIntervalAccountFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ) => Promise<OpResult>;
  set: (params: ServiceIntervalAccountInput) => Promise<OpResult>;
  remove: (carId: string, kindId: number) => Promise<OpResult>;
  removeMany: (items: ServiceIntervalAccountWhereInput[]) => Promise<OpResult>;

  // Additional convenience methods
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedServiceIntervalAccount: (
    interval: ServiceIntervalAccount | null
  ) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getServiceIntervalAccountByKindId: (
    kindId: number | undefined
  ) => ServiceIntervalAccount | undefined;
  getCustomizedIntervals: () => ServiceIntervalAccount[];
  getDefaultIntervals: () => ServiceIntervalAccount[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "kindId", order: "ASC" }];
const DEFAULT_FILTERS: Partial<ServiceIntervalAccountFilter> = {};

// ============================================
// Hook Implementation
// ============================================

export const useServiceIntervalAccounts = (
  options: UseServiceIntervalAccountsOptions = {}
): UseServiceIntervalAccountsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = false,
    carId = null,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  // These are read-only after mount. Used by:
  //   - The userId/carId-change effect (for autoLoad re-fetches)
  //   - refresh (pageSize and sort)
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters for refresh
  const lastQueriedFiltersRef =
    useRef<Partial<ServiceIntervalAccountFilter>>(initialFilters);

  // Ref to track the last-queried sort for refresh
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId and carId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const previousCarIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [serviceIntervalAccounts, setServiceIntervalAccounts] = useState<
    ServiceIntervalAccount[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Search and filters — tracks what was last queried
  const [filters, setFilters] =
    useState<Partial<ServiceIntervalAccountFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Operations state
  const [isSaving, setIsSaving] = useState(false);
  const [removingKindId, setRemovingKindId] = useState<number | null>(null);
  const [selectedServiceIntervalAccount, setSelectedServiceIntervalAccount] =
    useState<ServiceIntervalAccount | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && serviceIntervalAccounts.length === 0,
    [isLoaded, serviceIntervalAccounts.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  // Helper function to update item in list by kindId
  const updateInListByKindId = useCallback(
    (updatedItem: ServiceIntervalAccount) => {
      setServiceIntervalAccounts((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.kindId === updatedItem.kindId
        );
        if (existingIndex >= 0) {
          const newItems = [...prev];
          newItems[existingIndex] = updatedItem;
          return newItems;
        }
        return prev;
      });
    },
    []
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls
    listCallIdRef.current++;

    setServiceIntervalAccounts([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setIsSaving(false);
    setRemovingKindId(null);
    setSelectedServiceIntervalAccount(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  /**
   * List service interval accounts.
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   */
  const list = useCallback(
    async (
      filter: ServiceIntervalAccountFilter,
      pagination?: Pagination,
      sortByParams?: SortItem[]
    ): Promise<OpResult> => {
      if (!filter.carId) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Car ID is required to list service intervals"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      const callId = ++listCallIdRef.current;

      setLoading(true);
      setLastError(null);

      // Store the queried filters/sort so refresh can use them via refs
      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        lastQueriedSortRef.current = sortByParams;
      }

      const currentSortBy = sortByParams || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination | undefined = pagination
          ? { page: pagination.page, pageSize: pagination.pageSize }
          : undefined;

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await ServiceIntervalAccountService.list(
          filter,
          paginationParams,
          sortParams
        );

        // Guard: if a newer list() call was initiated, discard this response
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const items = Array.isArray(result.data)
            ? (result.data as ServiceIntervalAccount[])
            : [];
          setServiceIntervalAccounts(items);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to load service intervals")
        );
        setIsLoaded(true);
        setServiceIntervalAccounts([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to load service intervals")
        );
        setIsLoaded(true);
        setServiceIntervalAccounts([]);
        return result;
      } finally {
        // Only update loading flag if this call is still the latest
        if (callId === listCallIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Set (create or update) service interval account
  const set = useCallback(
    async (params: ServiceIntervalAccountInput): Promise<OpResult> => {
      setIsSaving(true);
      setLastError(null);

      try {
        const result = await ServiceIntervalAccountService.set(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const savedItem = (result.data as ServiceIntervalAccount[])[0];
          updateInListByKindId(savedItem);
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to save service interval")
        );
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to save service interval")
        );
        return result;
      } finally {
        setIsSaving(false);
      }
    },
    [updateInListByKindId]
  );

  // Remove service interval account (revert to default)
  const remove = useCallback(
    async (removeCarId: string, kindId: number): Promise<OpResult> => {
      if (!removeCarId || kindId == null) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Car ID and Kind ID are required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingKindId(kindId);
      setLastError(null);

      try {
        const result = await ServiceIntervalAccountService.remove(
          removeCarId,
          kindId
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          // The API returns the default interval after removal
          const defaultItem = (result.data as ServiceIntervalAccount[])[0];
          if (defaultItem) {
            updateInListByKindId(defaultItem);
          }
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(
            result,
            "Failed to remove service interval customization"
          )
        );
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(
            error,
            "Failed to remove service interval customization"
          )
        );
        return result;
      } finally {
        setRemovingKindId(null);
      }
    },
    [updateInListByKindId]
  );

  // Remove many service interval accounts (revert to defaults)
  const removeMany = useCallback(
    async (items: ServiceIntervalAccountWhereInput[]): Promise<OpResult> => {
      if (items.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one service interval is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await ServiceIntervalAccountService.removeMany(items);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const defaultItems = Array.isArray(result.data)
            ? (result.data as ServiceIntervalAccount[])
            : [];
          defaultItems.forEach((item) => {
            updateInListByKindId(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(
            result,
            "Failed to remove service interval customizations"
          )
        );
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(
            error,
            "Failed to remove service interval customizations"
          )
        );
        return result;
      }
    },
    [updateInListByKindId]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Refresh service interval accounts
  // Uses refs so it always picks up the last-queried filters/sort
  const refresh = useCallback(async (): Promise<OpResult> => {
    const currentFilters = lastQueriedFiltersRef.current;

    if (!currentFilters.carId && !carId) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "Car ID is required to refresh service intervals"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    const filterWithCar = {
      ...currentFilters,
      carId: currentFilters.carId || carId,
    } as ServiceIntervalAccountFilter;

    return list(filterWithCar, undefined, lastQueriedSortRef.current);
  }, [list, carId]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  // Lookup helper - find by kindId
  const getServiceIntervalAccountByKindId = useCallback(
    (kindId: number | undefined): ServiceIntervalAccount | undefined => {
      if (kindId == null) return undefined;
      return serviceIntervalAccounts.find((item) => item.kindId === kindId);
    },
    [serviceIntervalAccounts]
  );

  // Lookup helper - get all customized intervals
  const getCustomizedIntervals = useCallback(
    (): ServiceIntervalAccount[] => {
      return serviceIntervalAccounts.filter(
        (item) => item.isCustomized === true
      );
    },
    [serviceIntervalAccounts]
  );

  // Lookup helper - get all default (non-customized) intervals
  const getDefaultIntervals = useCallback(
    (): ServiceIntervalAccount[] => {
      return serviceIntervalAccounts.filter(
        (item) => item.isCustomized !== true
      );
    },
    [serviceIntervalAccounts]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Handle user or car change - reset and reload data
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    const previousCarId = previousCarIdRef.current;
    previousUserIdRef.current = userId;
    previousCarIdRef.current = carId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled, user is logged in, and carId is provided
      if (autoLoad && userId && carId) {
        list(
          {
            carId,
            ...initialFiltersRef.current,
          } as ServiceIntervalAccountFilter,
          undefined,
          initialSortRef.current
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId && carId) {
        list(
          {
            carId,
            ...initialFiltersRef.current,
          } as ServiceIntervalAccountFilter,
          undefined,
          initialSortRef.current
        );
      }
      return;
    }

    // Car changed
    if (previousCarId !== carId) {
      reset();

      if (autoLoad && userId && carId) {
        list(
          {
            carId,
            ...initialFiltersRef.current,
          } as ServiceIntervalAccountFilter,
          undefined,
          initialSortRef.current
        );
      }
    }
  }, [userId, carId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      serviceIntervalAccounts,
      loading,
      isLoaded,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      pageSize: pageSizeRef.current,

      // Operations state
      isSaving,
      removingKindId,
      selectedServiceIntervalAccount,

      // Core operations
      list,
      set,
      remove,
      removeMany,

      // Additional convenience methods
      refresh,

      // UI state management
      setSelectedServiceIntervalAccount,

      // Helper operations
      reset,

      // Lookup helpers
      getServiceIntervalAccountByKindId,
      getCustomizedIntervals,
      getDefaultIntervals,

      // Error handling
      lastError,
      clearError,
    }),
    [
      serviceIntervalAccounts,
      loading,
      isLoaded,
      isEmpty,
      filters,
      sortBy,
      isSaving,
      removingKindId,
      selectedServiceIntervalAccount,
      list,
      set,
      remove,
      removeMany,
      refresh,
      getServiceIntervalAccountByKindId,
      getCustomizedIntervals,
      getDefaultIntervals,
      lastError,
      clearError,
      reset,
    ]
  );
};